// @givmo/board — what a host supplies so the director pages can run (CBG-SEC2 SEC2-WEB).
//
// The package owns no sign-in. A director is the console's own signed-in account (D-CBG-16), so
// everything that touches the account — who is signed in, the ways in, signing out, Account
// Security's setup offer and Sign-in & security card — is the host's, handed in here. The pages
// place them; they never build one.
//
// The shared "Confirm it's you" sheet is NOT here: its host is `@givmo/step-up`'s
// `StepUpHostProvider`, which the host renders around the director pages too, and whose adapter is
// also where `holdsAuthenticator` lives — the sheet chooses its arms from that adapter, so a Sign
// button that chooses between the sheet and the setup offer reads the same source.

import { createContext, use, type ReactNode } from 'react';

/** The signed-in account, as the console knows it — never the seat's address. */
export interface BoardDirectorAccount {
  displayName: string | null;
  email: string | null;
  /**
   * The account's stable id, never shown. A director's unsent words are kept per account
   * (`director-drafts.ts`); absent, no draft is kept.
   */
  uid?: string;
}

/**
 * Where the ways in are shown. An invitation's visitors are mostly new, so "Continue with email" opens
 * the create form there; the door's are returning, so it opens the sign-in form. `another_account` is
 * Board's two "use another Givmo account" steps (the invitation's hint step, the move's sign-in step):
 * the door's ways in without "Continue with Organization SSO", which would lead straight back to the
 * account just left (Account Security's AS-WEB30 rider); email opens the sign-in form.
 */
export type BoardDirectorArrival = 'invitation' | 'door' | 'another_account';

export interface BoardDirectorWaysInProps {
  arrival: BoardDirectorArrival;
  /** The address to come back to once signed in (the invitation, or the page the guard bounced). */
  returnTo: string;
  /**
   * Called when the visitor picks a way in, before the sign-in starts: on the invitation, choosing
   * to sign in IS choosing to join (D-CBG-18 answer 3), so the landing records that intent here.
   */
  onChosen?: () => void;
  /** The invitation's two facts, for the create form's lines; absent at the door. */
  invitation?: { organizationName: string | null; maskedEmailOfRecord: string };
  /**
   * CBG-SEC2-L4-MOVE: the move's two facts, for the create form's lines when the director crosses to
   * it from the sign-in form ("Then your seat on the … board moves to it.", frame b3c).
   */
  move?: BoardDirectorSeatMoveFacts;
  /**
   * One line for Account Security's lock notice (AS-WEB27's rider, Board's derivation §6 (4)): when an
   * organization's sign-in lock refuses a way in here, the console's notice places this after its
   * "Continue with Organization SSO" and before its unlink paragraph. Board's to word and to give —
   * only from a board invitation, and not where the board is covered by its own organization's
   * sign-in (D-AS-46). Absent, the notice has no such line.
   */
  lockNoticeLine?: ReactNode;
}

/**
 * CBG-SEC2-L4 (D-CBG-23; frames a1, a2, a2c): "Create new account" for a director whose Givmo account
 * can't hold the seat, because another organization's sign-in lock binds it.
 */
export interface BoardDirectorCreateAccountProps {
  /** The address to come back to once the new account is signed in (the invitation, or the move). */
  returnTo: string;
  /** The invitation's two facts, for the create form's lines. */
  invitation?: { organizationName: string | null; maskedEmailOfRecord: string };
  /**
   * CBG-SEC2-L4-MOVE (frame b3c): the move's two facts instead, for the form's two lines in the move
   * ("Then your seat on the … board moves to it.", "Your seat’s address is …").
   */
  move?: BoardDirectorSeatMoveFacts;
}

/**
 * CBG-SEC2-L4-MOVE (D-CBG-23 flow (b)): what the create form says about a seat being moved — the
 * board's name, and the seat's address, masked. Never the move's ticket, which stays in Board's tab.
 */
export interface BoardDirectorSeatMoveFacts {
  organizationName: string | null;
  maskedEmailOfRecord: string;
}

/** How Account Security's setup offer ended (SEC2-AUTHWEB's `PasskeySetupOffer`). */
export type BoardDirectorSetupOutcome = 'created' | 'declined' | 'unavailable';

export interface BoardDirectorSetupOfferProps {
  /** `from-sign` carries the offer's "Not now. Nothing is signed until you set one up." (D-AS-29). */
  variant: 'default' | 'from-sign';
  onSettled: (outcome: BoardDirectorSetupOutcome) => void;
}

/**
 * Account Security's setup offer (A1), as the approved frames place it: its title and line as the
 * page's heading in Board's shell, and its card beneath (`m1-joined`, `w-setup-2-offer`). The words
 * and the card are theirs; Board only places them.
 */
export interface BoardDirectorSetupOffer {
  title: string;
  description: string;
  render: (props: BoardDirectorSetupOfferProps) => ReactNode;
}

/**
 * The person's own "Sign-in & security" (Account Security's A4 card): the passkeys of the Givmo
 * account they sign in with everywhere. A director who isn't one of the organization's officers has no
 * Settings to reach it through, so the account menu leads to a director page that places it — its
 * title and line as the page's heading, the card beneath. The words and the card are theirs.
 */
export interface BoardDirectorSignInSecurity {
  title: string;
  description: string;
  /**
   * Whether the card has anything to show here: its own dark gate (the passkey list answering). False,
   * the account menu offers no row, as the console's own menu does.
   */
  available: boolean;
  /**
   * Read what `available` rests on, as the director reaches for the account menu: the pointer on its
   * trigger, the trigger's own focus, the menu opening (AS-WEB16). Never on a page load or the
   * window's return, which was a request per page, and a 404 per page wherever passkeys are dark.
   */
  read: () => void;
  render: () => ReactNode;
}

export interface BoardDirectorHost {
  /** The signed-in account, or null when nobody is signed in. */
  account: BoardDirectorAccount | null;
  /** The console's ways in — Google, Microsoft, email and, where it can run, a passkey — equal in weight. */
  renderWaysIn: (props: BoardDirectorWaysInProps) => ReactNode;
  /** Sign out of the console, then return to `returnTo` when given. Signing out ends the window. */
  signOut: (returnTo?: string) => Promise<void>;
  /**
   * CBG-SEC2-L4 (D-CBG-23): open the console's create form for ANOTHER Givmo account, with the
   * invitation's lines (or, CBG-SEC2-L4-MOVE, the move's), and with Board's "use another Givmo
   * account" arrival carried through the
   * console's email forms, whose notices then offer no organization door (it would lead back to the
   * account just left). For a visitor who is signed out: Board signs a signed-in account out first,
   * through `signOut`, and asks for the form from the page that comes back signed out.
   */
  createAnotherAccount: (props: BoardDirectorCreateAccountProps) => Promise<void>;
  /**
   * CBG-SEC2-L4: the organization whose sign-in lock binds the signed-in account, for Board's pointer
   * words (frame a1). Account Security's rule: named only where the account's own security read says
   * that organization's lock is the one that binds; `null` or absent names no one ("Your organization
   * …"). From what the console already holds; Board warms it through the step-up host's `prepare`
   * when its pointer shows.
   */
  lockingOrganizationName?: string | null;
  /**
   * CBG-SEC2-L4: what this console calls the kind of organization whose sign-in lock keeps an account
   * from a board, in Board's two signed-out lines (frames a0 and a2). The package names no class, so
   * the word is the host's, as the class words are.
   */
  lockingOrganizationNoun: string;
  /** Account Security's setup offer (A1), or null for a host that has none to show. */
  setupOffer: BoardDirectorSetupOffer | null;
  /** Account Security's Sign-in & security card (A4), or null for a host that has none to show. */
  signInSecurity: BoardDirectorSignInSecurity | null;
  /**
   * The device's own word for a passkey, by Account Security's rule ("Touch ID", "Face ID", …), or
   * the generic "a passkey" where the host can't tell.
   */
  methodWord: string;
}

const DirectorHostContext = createContext<BoardDirectorHost | null>(null);

export function BoardDirectorHostProvider({
  value,
  children,
}: {
  value: BoardDirectorHost;
  children: ReactNode;
}) {
  return <DirectorHostContext value={value}>{children}</DirectorHostContext>;
}

/** The host's half. A director page outside a host is a wiring defect, so it throws. */
export function useBoardDirectorHost(): BoardDirectorHost {
  const host = use(DirectorHostContext);
  if (host === null)
    throw new Error('Board’s director pages need a host (BoardDirectorHostProvider).');
  return host;
}
